import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ENROLLMENT_ACTIONS } from '../../modals/modalTypes';
import { IGroupItems } from '../../types/customGroupingTypes';
import useCustomGrouping from '../../hooks/useCustomGrouping';
import MemberCard, { IMemberCardConfig } from '../common/MemberCard';
import { useAppDispatch, useAppSelector, useModal, useExchangeConditions } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';
import { formatCurrency } from '@utils/tools/formatCurrenyToInteger';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import {
    EnrolledGroupCTAButtonTypes,
    GroupCSRLevels,
    PlAN_TYPES,
} from '../../../../constants/individualPortal/customGrouping/customGrouping.constants';
import { capitalize } from '@utils/miscellaneous/objectHelpers';

interface IEnrolledGroupCardProps {
    group: IGroupItems;
    planType: PlAN_TYPES;
}

function CardHeader({
    isAPTCVisible,
    isStateSubsidyVisible,
    group,
    coverageYear,
}: {
    isAPTCVisible: boolean;
    isStateSubsidyVisible: boolean;
    group: IGroupItems;
    coverageYear: number;
}) {
    const { isChangePlanMessageVisible } = useExchangeConditions();
    const isStateSubsidyMsgVisible = !!(
        group.hasAPTCEligibleMembers &&
        group.hasStateSubsidyEligibleMembers &&
        group.electedStateSubsidy
    );

    return (
        <p data-testid={`enrolled_group_header_${group.id}`}>
            <ConditionalRendering isTrue={isAPTCVisible || isStateSubsidyVisible}>
                <ConditionalRendering isTrue={isAPTCVisible}>
                    <FormattedMessage
                        id="customGrouping.APTC"
                        values={{ amount: formatCurrency(group.maxAptc || 0, false) }}
                    />{' '}
                </ConditionalRendering>
                <ConditionalRendering isTrue={isStateSubsidyVisible}>
                    <ConditionalRendering isTrue={isAPTCVisible}>
                        <FormattedMessage id="customGrouping.stateSubsidyMsg1" />{' '}
                    </ConditionalRendering>
                    <FormattedMessage
                        id="customGrouping.stateSubsidyMsg2"
                        values={{ amount: formatCurrency(group.maxStateSubsidy || 0, false) }}
                    />{' '}
                </ConditionalRendering>
                <FormattedMessage 
                    id="customGrouping.APTCMsg" 
                    values={{ 
                        strong: (chunks) => <strong>{chunks}</strong>
                    }}
                />{' '}
            </ConditionalRendering>
            <ConditionalRendering isTrue={!isAPTCVisible && !isStateSubsidyVisible}>
                <ConditionalRendering
                    isTrue={(group.hasAPTCEligibleMembers || group.hasStateSubsidyEligibleMembers) && !group.isRenewal}
                >
                    <FormattedMessage id="customGrouping.APTCSuccess" />{' '}
                    <ConditionalRendering isTrue={group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN}>
                        <ConditionalRendering isTrue={isChangePlanMessageVisible}>
                            <FormattedMessage id="customGrouping.ChangePlanMsg" />{' '}
                        </ConditionalRendering>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!!group.electedAptc}>
                        <FormattedMessage
                            id="customGrouping.APTCMsg2"
                            values={{ 
                                amount: formatCurrency(group.electedAptc || 0, false),
                                strong: (chunks) => <strong>{chunks}</strong>,
                            }}
                        />{' '}
                        <ConditionalRendering isTrue={isStateSubsidyMsgVisible}>
                            <FormattedMessage id="customGrouping.stateSubsidyMsg1" />{' '}
                        </ConditionalRendering>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={!!group.electedStateSubsidy}>
                        <FormattedMessage
                            id="customGrouping.stateSubsidyMsg2"
                            values={{ amount: formatCurrency(group.electedStateSubsidy || 0, false) }}
                        />{' '}
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={group.electedAptc > 0 || group.electedStateSubsidy > 0}>
                        <FormattedMessage id="customGrouping.APTCMsg3" />
                    </ConditionalRendering>
                </ConditionalRendering>

                <ConditionalRendering
                    isTrue={(group.hasAPTCEligibleMembers || group.hasStateSubsidyEligibleMembers) && group.isRenewal}
                >
                    <ConditionalRendering isTrue={!!group.maxAptc}>
                        <FormattedMessage
                            id="customGrouping.APTCMsgRenewal"
                            values={{ coverageYear, amount: formatCurrency(group.maxAptc || 0, false) }}
                        />{' '}
                        <ConditionalRendering
                            isTrue={
                                !!(
                                    group.hasAPTCEligibleMembers &&
                                    group.hasStateSubsidyEligibleMembers &&
                                    group.maxStateSubsidy
                                )
                            }
                        >
                            <FormattedMessage id="customGrouping.stateSubsidyMsg1" />
                        </ConditionalRendering>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={!!group.maxStateSubsidy}>
                        <FormattedMessage
                            id="customGrouping.stateSubsidyMsgRenewal"
                            values={{ amount: formatCurrency(group.maxStateSubsidy || 0, false), coverageYear }}
                        />{' '}
                    </ConditionalRendering>
                </ConditionalRendering>

                <ConditionalRendering
                    isTrue={!group.hasAPTCEligibleMembers && !group.hasStateSubsidyEligibleMembers && !group.isRenewal}
                >
                    <FormattedMessage id="customGrouping.APTCSuccess" />
                    <ConditionalRendering isTrue={group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN}>
                        <ConditionalRendering isTrue={isChangePlanMessageVisible}>
                            {' '}<FormattedMessage id="customGrouping.ChangePlanMsg" />
                        </ConditionalRendering>
                    </ConditionalRendering>
                </ConditionalRendering>
            </ConditionalRendering>
        </p>
    );
}

export default function EnrolledGroupCard(props: IEnrolledGroupCardProps) {
    const environment = useAppSelector(environmentSelector);
    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const dispatch = useAppDispatch();
    const { disenrollPlan, changePlan, showAddAPTCMessage, showAddStateSubsidyMessage } = useCustomGrouping({
        dispatch,
    });
    const { coverageYear, localDates } = environment;
    const { serverDateInUTC } = localDates;

    const { disenrollDialogDataList = [], enablePopupForEndOfPHE, caseNumber } = customGroupingState;
    const { openModal } = useModal();

    const isAPTCVisible = showAddAPTCMessage(props.group);
    const isStateSubsidyVisible = showAddStateSubsidyMessage(props.group);

    const { group, planType } = props;

    const primaryMembers: IMemberCardConfig[] = [];
    const secondaryMembers: IMemberCardConfig[] = [];
    const planDetails = {
        headers: getPlanDetailsHeader(),
        issuerName: group.issuerName,
        planName: group.planName,
        netPremium: group.netPremium || group.netPremium === 0 ? 'customGrouping.netPremium' : '',
        perimoumAmount: formatCurrency(group.netPremium, false),
    };

    // Define the onClick handler separately for better readability and reusability
    const handleCTAClick = () => {
        const groupToChange = {
            ...group,
            memberList: group.memberList.filter(
                member =>
                    (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
                    (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId),
            ),
        };
        changePlan({ typeOfPlan: planType, groupToChange, enablePopupForEndOfPHE, caseNumber }, { openModal });
    };

    // Determine the CTA configuration based on the button type
    const primaryCTA =
        group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_BLANK
            ? undefined
            : {
                label: group.ctaButton.btnText,
                onClick: handleCTAClick,
            };

    function getPlanDetailsHeader() {
        if (props.planType === PlAN_TYPES.HEALTH) {
            if (props.group.groupCsr && props.group.groupCsr !== GroupCSRLevels.CS1) {
                return [{ header: 'customGrouping.groupCsr' }];
            }
        } else {
            if (group.electedAptc > 0) {
                if (group.isRenewal) {
                    return [
                        {
                            header: 'customGrouping.APTCMsgRenewal',
                            headerValues: { amount: formatCurrency(group.maxAptc || 0, false), coverageYear },
                        },
                    ];
                } else {
                    return [
                        {
                            header: 'customGrouping.totalAPTC',
                            headerValues: { amount: formatCurrency(group.electedAptc || 0, false) },
                        },
                    ];
                }
            }
        }
        return [];
    }

    function disenrollOnClickHandler(enrollmentAction: ENROLLMENT_ACTIONS) {
        const disenrollGroup = disenrollDialogDataList.find(
            data => data.groupEnrollmentId === group.encryptedGroupEnrollmentId,
        );
        disenrollPlan({
            caseNumber,
            coverageStartDate: group.coverageDate,
            typeOfPlan: planType,
            enrollmentId: group.encryptedGroupEnrollmentId,
            group: disenrollGroup,
            enrollmentAction,
            serverDateInUTC,
            openModal,
        });
    }

    function getSecondaryCTA() {
        if (group.isRenewal) return undefined;
        if (group.validCoverageSDate) {
            return {
                label: 'customGrouping.disenroll',
                onClick: () => disenrollOnClickHandler(ENROLLMENT_ACTIONS.SUBMIT),
            };
        } else if (group.enrollmentStatus) {
            if (['pending', 'confirm'].includes(group.enrollmentStatus?.toLowerCase()))
                return {
                    label: 'customGrouping.canceCoverage',
                    onClick: () => disenrollOnClickHandler(ENROLLMENT_ACTIONS.CACNEL),
                };
        }
    }

    group.memberList.forEach(member => {
        if (
            (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
            (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
        ) {
            primaryMembers.push({
                data: member,
                config: {
                    isCheckBoxVisible: group.isRenewal,
                    isChecked: false,
                },
            });
        }
    });

    group.inEligibleMemberList.forEach(member => {
        if (
            (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
            (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
        ) {
            secondaryMembers.push({
                data: member,
                config: {
                    isCheckBoxVisible: group.isRenewal,
                    isChecked: false,
                    tooltipTitle: 'customGrouping.ineligibleMember',
                    tooltipBody: group.memberList.length
                        ? 'customGrouping.ineligibleMember.tooltip'
                        : 'customGrouping.ineligibleGroupMember.tooltip',
                    tooltipValues: { planType: capitalize(planType), exchangeName: environment.exchangeName },
                },
            });
        }
    });

    return (
        <div className="padding">
            <CardHeader
                isAPTCVisible={isAPTCVisible}
                isStateSubsidyVisible={isStateSubsidyVisible}
                group={group}
                coverageYear={coverageYear}
            />

            <div className="row-fluid">
                <MemberCard
                    id={String(group.id)}
                    primaryMembers={primaryMembers}
                    secondaryMembers={secondaryMembers}
                    planDetails={planDetails}
                    primaryCTA={primaryCTA}
                    secondaryCTA={getSecondaryCTA()}
                />
            </div>
        </div>
    );
}
